import { redirectTo } from '@app/routes/helpers';
import env from '@config/env';
import logger from '@libs/log';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { Api } from '@state/utils/Api';
import * as atatus from 'atatus-spa';
import { AxiosResponse, type AxiosError, type AxiosRequestConfig } from 'axios';
import camelcaseKeys from 'camelcase-keys';
import posthog from 'posthog-js';
import { useDispatch, useSelector } from 'react-redux';
import reduxLogger from 'redux-logger';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  persistReducer,
  persistStore,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import authReducer, { logoutThunk, refresh, updateLocation } from './slices/auth.slice';
import projectsReducer from './slices/project.slice';
import usersReducer from './slices/user.slice';
import workspaceReducer from './slices/workspace.slice';

const persistConfig = {
  key: 'root',
  storage,
  version: 1,
  timeout: 100,
  throttle: 100,
  debug: env.DEV || env.ROLLBAR_ENV === 'development',
  blacklist: ['projects'],
};

const reducer = combineReducers({
  auth: authReducer,
  users: usersReducer,
  workspaces: workspaceReducer,
  projects: projectsReducer,
});

const persistedReducer = persistReducer(persistConfig, reducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) => {
    const middlewares = getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    });
    return env.DEV || env.ROLLBAR_ENV === 'development'
      ? middlewares.concat(reduxLogger)
      : middlewares;
  },
  devTools: env.DEV || env.ROLLBAR_ENV === 'development',
});

export type RootState = ReturnType<typeof store.getState>;

export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = useDispatch.withTypes<AppDispatch>();

export const useAppSelector = useSelector.withTypes<RootState>();

export const persistor = persistStore(store, null, () => {
  // Callback doesn't receive error parameter in the expected type
  // If error handling is needed, use .catch() or subscribe to persistor events
});

export const logout = () => {
  // logout user action in auth slice
  store
    .dispatch(logoutThunk())
    .then(() => {
      // clear update location action in auth slice
      store.dispatch(
        updateLocation({
          pathname: '/',
          search: '',
          hash: '',
          key: '',
        })
      );

      // clear local storage
      localStorage.clear();

      // clear redux store
      persistor.purge().catch(() => {
        logger.error('Failed to purge the store');
      });

      // redirect to sign in page
      posthog.reset();
      Sentry.setUser(null);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      atatus.setUser('', '', '');
      redirectTo('/sign_in');
      // window.location.reload();
    })
    .catch(() => {
      logger.error('Failed to logout');
    });
};

interface RetryableRequest extends AxiosRequestConfig {
  _retry?: boolean;
}

// Interceptor to convert the response data to camelCase
Api.interceptors.response.use(
  (response: AxiosResponse) => {
    // If the response data is an object and the content type is application/json, convert the data to camelCase
    if (
      response.data &&
      (response.headers['content-type'] as string).includes('application/json')
    ) {
      // If the response data is an object and the content type is application/json, convert the data to camelCase
      if (typeof response.data === 'object' && response.data !== null) {
        response.data = camelcaseKeys(response.data as Record<string, unknown>, { deep: true });
      }
    }
    return response;
  },
  (error: AxiosError) => {
    // If the error is a retryable request, retry the request
    const originalRequest = error.config as RetryableRequest;
    // If the error is a 401 and the request is to the oauth/token endpoint, logout the user
    if (error.response?.status === 401 && originalRequest.url === '/auth/refresh') {
      logout();
      return Promise.reject(error);
    }
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      store
        .dispatch(refresh())
        .then(() => {
          return Api(originalRequest);
        })
        .catch(() => {
          logout();
          return Promise.reject(error);
        });
    }
    return Promise.reject(error);
  }
);
